package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/pref_store")
@Composable
fun PrefStoreComposePage() {
    BlogLayout(
        PageProperties(
            "Preferences DataStore",
            ""
        ),
        BlogProperties("Jetpack Compose", "Preferences DataStore")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li {
                    SpanText(
                        text = "DataStore is a new and improved data storage solution aimed at replacing SharedPreferences. Built on Kotlin coroutines and Flow, DataStore provides two different implementations: Proto DataStore, that stores typed objects (backed by protocol buffers) and Preferences DataStore, that stores key-value pairs. Data is stored asynchronously, consistently, and transactionally, overcoming some of the drawbacks of SharedPreferences.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium))
                }
            }

            SpanText(
                text = "2 types of DataStore",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Preference DataStore",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Used to store Data in Key Value format (Just like in Shared Preference).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Doesn’t need a pre-defined schema like SQL or Proto-DataStore.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Doesn’t provide type-safety.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Proto DataStore",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Used to store data in custom data format (Complex/Custom Datatypes).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Need to define schema using Protobuffers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Provides type-safety.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Quickly recap",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provides a fully asynchronous API for retrieving and saving data, using the power of Kotlin coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Does not offer ready-to-use synchronous support — it directly avoids doing any work that blocks the UI thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Relies on Flow’s inner error signaling mechanism, allowing you to safely catch and handle exceptions when reading or writing data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handles data updates transactionally in an atomic read-modify-write operation, providing strong ACID guarantees.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Allows easy and quick data migrations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Wish to quickly migrate from SharedPreferences with minimal changes and feel confident enough without full type safety? Choose Preferences over Proto.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "1 - Preferences DataStore",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed class Pref<T>(val key: String)

                    class IntPref(key: String) : Pref<Int>(key)
                    class StringPref(key: String) : Pref<String>(key)
                    class BooleanPref(key: String) : Pref<Boolean>(key)
                    class FloatPref(key: String) : Pref<Float>(key)
                    class ObjectPref<T>(key: String, val clazz: Class<T>) : Pref<T>(key)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.coroutines.flow.Flow

                    interface PreferenceDataStore {
                        suspend fun <T> storePref(pref: Pref<T>, value: T)
                        fun <T> loadPref(pref: Pref<T>): Flow<T?>
                        fun <T> loadPref(pref: Pref<T>, default: T): Flow<T>
                        suspend fun <T> removePref(pref: Pref<T>)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences
                    import androidx.datastore.preferences.core.booleanPreferencesKey
                    import androidx.datastore.preferences.core.edit
                    import androidx.datastore.preferences.core.floatPreferencesKey
                    import androidx.datastore.preferences.core.intPreferencesKey
                    import androidx.datastore.preferences.core.stringPreferencesKey
                    import androidx.datastore.preferences.core.stringSetPreferencesKey
                    import com.squareup.moshi.Moshi
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.distinctUntilChanged
                    import kotlinx.coroutines.flow.map
                    import javax.inject.Inject

                    class PreferenceDataStoreImpl @Inject constructor(
                        private val dataStore: DataStore<Preferences>,
                        private val moshi: Moshi,
                    ) : PreferenceDataStore {

                        override suspend fun <T> storePref(pref: Pref<T>, value: T) {
                            dataStore.edit { preferences ->
                                when (pref) {
                                    is IntPref -> preferences[intPreferencesKey(pref.key)] = value as Int
                                    is StringPref -> preferences[stringPreferencesKey(pref.key)] = value as String
                                    is BooleanPref -> preferences[booleanPreferencesKey(pref.key)] = value as Boolean
                                    is FloatPref -> preferences[floatPreferencesKey(pref.key)] = value as Float
                                    is ObjectPref -> preferences[stringPreferencesKey(pref.key)] =
                                        moshi.adapter(pref.clazz).toJson(value)
                                }
                            }
                        }

                        @Suppress("UNCHECKED_CAST")
                        override fun <T> loadPref(pref: Pref<T>): Flow<T?> {
                            val key = createKey(pref)
                            return dataStore.data.map { preferences ->
                                val value = preferences[key]
                                try {
                                    when (pref) {
                                        is ObjectPref -> moshi.adapter(pref.clazz).fromJson(value.toString())
                                        else -> value as? T
                                    }
                                } catch (e: Exception) {
                                    null
                                }
                            }.distinctUntilChanged()
                        }

                        override fun <T> loadPref(pref: Pref<T>, default: T): Flow<T> =
                            loadPref(pref).map { it ?: default }

                        override suspend fun <T> removePref(pref: Pref<T>) {
                            val key = createKey(pref)
                            dataStore.edit { preferences ->
                                preferences.remove(key)
                            }
                        }

                        private fun createKey(pref: Pref<*>): Preferences.Key<*> {
                            return when (pref) {
                                is IntPref -> intPreferencesKey(pref.key)
                                is StringPref,
                                is ObjectPref,
                                -> stringSetPreferencesKey(pref.key)
                                is BooleanPref -> booleanPreferencesKey(pref.key)
                                is FloatPref -> floatPreferencesKey(pref.key)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @InstallIn(SingletonComponent::class)
                    @Module
                    object DatastoreRepositoryModuleDI {
                        private const val FILE_NAME = "WEATHER_APP_PREFS"

                        @Singleton
                        @Provides
                        fun providesDataStore(@ApplicationContext appContext: Context): DataStore<Preferences> {
                            return PreferenceDataStoreFactory.create(
                                corruptionHandler = ReplaceFileCorruptionHandler(
                                    produceNewData = { emptyPreferences() }
                                ),
                                scope = CoroutineScope(Dispatchers.IO + SupervisorJob()),
                                produceFile = { appContext.preferencesDataStoreFile(FILE_NAME) }
                            )
                        }

                        @Singleton
                        @Provides
                        fun providesMoshi(): Moshi = Moshi.Builder().addLast(KotlinJsonAdapterFactory()).build()

                        @Singleton
                        @Provides
                        fun providesPreferenceDataStore(
                            dataStore: DataStore<Preferences>,
                            moshi: Moshi,
                        ): PreferenceDataStore = PreferenceDataStoreImpl(dataStore, moshi)

                        @Provides
                        fun providesDatastoreRepository(repository: DatastoreRepositoryImpl): DatastoreRepository {
                            return repository
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    interface DatastoreRepository {
                        val currentWeatherLocation: Flow<LocationEntity>
                        val currentDesign: Flow<Design>
                        suspend fun setCurrentWeatherLocation(currentWeatherLocation: LocationEntity)
                        suspend fun setCurrentDesign(currentDesign: Design)
                    }
                    
                    class DatastoreRepositoryImpl @Inject constructor(private val preferenceDataStore: PreferenceDataStore) :
                        DatastoreRepository {

                        override val currentWeatherLocation: Flow<LocationEntity> =
                            preferenceDataStore.loadPref(currentWeatherLocationPref, LocationEntity.DEFAULT)

                        override val currentDesign: Flow<Design> =
                            preferenceDataStore.loadPref(currentDesignPref, Design.CLASSIC)

                        override suspend fun setCurrentWeatherLocation(currentWeatherLocation: LocationEntity) =
                            preferenceDataStore.storePref(currentWeatherLocationPref, currentWeatherLocation)

                        override suspend fun setCurrentDesign(currentDesign: Design) {
                            preferenceDataStore.storePref(currentDesignPref, currentDesign)
                        }

                        companion object {
                            private val currentWeatherLocationPref = ObjectPref("current_weather_location", LocationEntity::class.java)
                            private val currentDesignPref = ObjectPref("current_design", Design::class.java)
                        }
                    }
                    
                    enum class Design {
                        CLASSIC, PUB
                    }
                    
                    data class LocationEntity(
                        val postcode: String?,
                        val city: String,
                        val label: String,
                        val lat: String,
                        val lon: String,
                    ) {
                        companion object {
                            val DEFAULT = LocationEntity(
                                postcode = "",
                                city = "",
                                label = " ",
                                lat = "",
                                lon = "",
                            )
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @HiltViewModel
                    class MainViewModel @Inject constructor(
                        private val datastoreRepository: DatastoreRepository,
                    ) : ViewModel() {
                        init {
                            viewModelScope.launch {
                                //set
                                //datastoreRepository.setCurrentDesign(currentDesign)
                                 
                               //get
                                datastoreRepository.currentDesign.first().let {
                                   
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "2 - Preferences DataStore Using Reified",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences
                    import androidx.datastore.preferences.core.booleanPreferencesKey
                    import androidx.datastore.preferences.core.doublePreferencesKey
                    import androidx.datastore.preferences.core.edit
                    import androidx.datastore.preferences.core.floatPreferencesKey
                    import androidx.datastore.preferences.core.intPreferencesKey
                    import androidx.datastore.preferences.core.longPreferencesKey
                    import androidx.datastore.preferences.core.stringPreferencesKey
                    import androidx.datastore.preferences.core.stringSetPreferencesKey
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.firstOrNull
                    import kotlinx.coroutines.flow.map

                    class DataStoreManager(val dataStore: DataStore<Preferences>) {

                     
                        suspend fun putString(key: String, value: String) {
                            put(key, value)
                        }

                        suspend fun putInt(key: String, value: Int) {
                            put(key, value)
                        }

                        suspend fun putDouble(key: String, value: Double) {
                            put(key, value)
                        }

                        suspend fun putBoolean(key: String, value: Boolean) {
                            put(key, value)
                        }

                        suspend fun putFloat(key: String, value: Float) {
                            put(key, value)
                        }

                        suspend fun putLong(key: String, value: Long) {
                            put(key, value)
                        }

                        suspend fun putSet(key: String, value: Set<String>) {
                            put(key, value)
                        }

                        suspend inline fun <reified T> put(key: String, value: T) {
                            dataStore.edit { preferences ->
                                when (value) {
                                    is String -> preferences[stringPreferencesKey(key)] = value
                                    is Int -> preferences[intPreferencesKey(key)] = value
                                    is Double -> preferences[doublePreferencesKey(key)] = value
                                    is Boolean -> preferences[booleanPreferencesKey(key)] = value
                                    is Float -> preferences[floatPreferencesKey(key)] = value
                                    is Long -> preferences[longPreferencesKey(key)] = value
                                    is Set<*> -> preferences[stringSetPreferencesKey(key)] = value as Set<String>
                                    else -> throw UnsupportedOperationException("Type not supported")
                                }
                            }
                        }

                        suspend inline fun <reified T> remove(key: String) {
                            val preferencesKey = getPreferencesKey<T>(key)
                            dataStore.edit {
                                it.remove(preferencesKey)
                            }
                        }

                        inline fun <reified T> getPreferencesKey(key: String): Preferences.Key<out Any> {
                            val preferencesKey = when (T::class) {
                                String::class -> stringPreferencesKey(key)
                                Int::class -> intPreferencesKey(key)
                                Double::class -> doublePreferencesKey(key)
                                Boolean::class -> booleanPreferencesKey(key)
                                Float::class -> floatPreferencesKey(key)
                                Long::class -> longPreferencesKey(key)
                                Set::class -> stringSetPreferencesKey(key)
                                else -> throw UnsupportedOperationException("Type not supported")
                            }
                            return preferencesKey
                        }

                        suspend fun getString(key: String, defaultValue: String): String? {
                            return getValue(key, defaultValue)
                        }

                        suspend fun getInt(key: String, defaultValue: Int): Int? {
                            return getValue(key, defaultValue)
                        }

                        suspend fun getDouble(key: String, defaultValue: Double): Double? {
                            return getValue(key, defaultValue)
                        }

                        suspend fun getBoolean(key: String, defaultValue: Boolean): Boolean? {
                            return getValue(key, defaultValue)
                        }

                        suspend fun getFloat(key: String, defaultValue: Float): Float? {
                            return getValue(key, defaultValue)
                        }

                        suspend fun getLong(key: String, defaultValue: Long): Long? {
                            return getValue(key, defaultValue)
                        }
                        
                        suspend fun getSet(key: String, defaultValue: Set<String>): Set<String>? {
                            return getValue(key, defaultValue)
                        }

                        suspend inline fun <reified T> getValue(key: String, defaultValue: T): T? {
                            return get(key, defaultValue).firstOrNull()
                        }

                        inline fun <reified T> get(key: String, defaultValue: T): Flow<T> {
                            val preferencesKey = getPreferencesKey<T>(key)
                            return dataStore.data.map { preferences ->
                                preferences[preferencesKey] as? T ?: defaultValue
                            }
                        }

                        inline fun <reified T> getOrNull(key: String): Flow<T?> {
                            val preferencesKey = getPreferencesKey<T>(key)
                            return dataStore.data.map { preferences ->
                                preferences[preferencesKey] as? T
                            }
                        }

                        val allData: Flow<Map<Preferences.Key<*>, Any>> = dataStore.data.map { preferences ->
                            preferences.asMap()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "2 - Preferences DataStore",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    interface DataStoreRepository {
                        suspend fun <T> saveInDataStore(key: Preferences.Key<T>, value: T)
                        suspend fun <T> getFromDataStore(key: Preferences.Key<T>): T?
                        suspend fun <T> deleteFromDataStore(key: Preferences.Key<T>)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import android.util.Log
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences
                    import androidx.datastore.preferences.core.edit
                    import com.mahmoudibrahem.omnicart.core.util.Constants.TAG
                    import com.mahmoudibrahem.omnicart.domain.repository.DataStoreRepository
                    import kotlinx.coroutines.flow.catch
                    import kotlinx.coroutines.flow.firstOrNull
                    import kotlinx.coroutines.flow.map
                    import javax.inject.Inject

                    class DataStoreRepositoryImpl @Inject constructor(
                        private val dataStore: DataStore<Preferences>
                    ) : DataStoreRepository {

                        override suspend fun <T> saveInDataStore(key: Preferences.Key<T>, value: T) {
                            dataStore.edit {
                                it[key] = value
                            }
                        }

                        override suspend fun <T> getFromDataStore(key: Preferences.Key<T>): T? {
                            return dataStore.data
                                .catch { throwable -> Log.d(TAG, throwable) }
                                .map { it[key] }
                                .firstOrNull()
                        }

                        override suspend fun <T> deleteFromDataStore(key: Preferences.Key<T>) {
                            dataStore.edit {
                                it.remove(key)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //di
                    private val Context.appDataStore by preferencesDataStore(name = APP_DATA_STORE_NAME)
                    const val APP_DATA_STORE_NAME = "DATA_STORE"

                    @Provides
                    @Singleton
                    fun provideDataStore(@ApplicationContext context: Context): DataStore<Preferences> {
                        return context.appDataStore
                    }
                """.trimIndent()
            )

            SpanText(
                text = "3 - DataStore KMP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/ben-burwood/Tasks
            KotlinCode(
                code = $$"""
                    actual val dataStoreModule = module {
                        single<DataStore<Preferences>> {
                            createDataStore(androidContext())
                        }
                    }
                    
                    private val koinModules = listOf(
                        dataStoreModule
                    )
                    
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.PreferenceDataStoreFactory
                    import androidx.datastore.preferences.core.Preferences
                    import androidx.datastore.preferences.core.edit
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.first
                    import kotlinx.coroutines.flow.map
                    import okio.Path.Companion.toPath

                    internal const val DATASTORE_NAME = "settings.preferences_pb"

                    fun createDataStore(producePath: () -> String): DataStore<Preferences> =
                        PreferenceDataStoreFactory.createWithPath(
                            produceFile = { producePath().toPath() }
                        )

                    /** Read a value from the DataStore for the given Key
                     * @param item The DataStoreItem (Key) to read
                     * @return The value of the DataStoreItem (Key) from the DataStore */
                    fun <T> DataStore<Preferences>.readAsFlow(item: DataStoreItem<T>): Flow<T?> {
                        return this.data.map { preferences ->
                            preferences[item.key]
                        }
                    }

                    /** Read a value from the DataStore for the given Key
                     * @param item The DataStoreItem (Key) to read
                     * @return The value of the DataStoreItem (Key) from the DataStore */
                    suspend fun <T> DataStore<Preferences>.read(item: DataStoreItem<T>): T? {
                        return this.readAsFlow(item).first()
                    }


                    /** Write a value to the DataStore for the given Key
                     * @param item The DataStoreItem (Key) to write
                     * @param value The value to write to the DataStoreItem (Key) */
                    suspend fun <T> DataStore<Preferences>.write(item: DataStoreItem<T>, value: T) {
                        this.edit { preferences ->
                            preferences[item.key] = value
                        }
                    }


                    /** Remove a value from the DataStore for the given Key
                     * @param item The DataStoreItem (Key) to remove */
                    suspend fun DataStore<Preferences>.remove(item: DataStoreItem<*>) {
                        this.edit { preferences ->
                            preferences.remove(item.key)
                        }
                    }


                    /** Clear all values from the DataStore */
                    suspend fun DataStore<Preferences>.clear() = this.edit { it.clear() }
                    
                    //DataStoreItem
                    sealed class DataStoreItem<T>(val key: Preferences.Key<T>) {
                        data object OATH_TOKEN : DataStoreItem<String>(stringPreferencesKey("oath_token"))

                        companion object {
                            suspend fun DataStore<Preferences>.getOathToken(): String? = this.read(OATH_TOKEN)
                        }
                    }
                    
                    class TaskListRemoteDataSource(
                        private val client: HttpClient,
                        private val datastore: DataStore<Preferences>
                    ) : TaskListDataSource { }
                    
                    //iosMain
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.Foundation.NSDocumentDirectory
                    import platform.Foundation.NSFileManager
                    import platform.Foundation.NSURL
                    import platform.Foundation.NSUserDomainMask


                    @OptIn(ExperimentalForeignApi::class)
                    fun createDataStore(): DataStore<Preferences> = createDataStore(
                        producePath = {
                            val directory: NSURL? = NSFileManager.defaultManager.URLForDirectory(
                                directory = NSDocumentDirectory,
                                inDomain = NSUserDomainMask,
                                appropriateForURL = null,
                                create = false,
                                error = null,
                            )
                            requireNotNull(directory).path + "/$DATASTORE_NAME"
                        }
                    )
                    
                    //androidMain
                    import android.content.Context
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences

                    fun createDataStore(context: Context): DataStore<Preferences> = createDataStore(
                        producePath = { context.filesDir.resolve(DATASTORE_NAME).absolutePath }
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "4 - KMP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect class Context
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //AppDataStore.kt

                    interface AppDataStore {
                        suspend fun setValue(
                            key: String,
                            value: String
                        )

                        suspend fun readValue(
                            key: String,
                        ): String?
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //AppDataStoreManager.kt

                    const val APP_DATASTORE = "package name"

                    class AppDataStoreManager(val context: Context) : AppDataStore {

                        override suspend fun setValue(
                            key: String,
                            value: String
                        ) {
                            context.putData(key, value)
                        }

                        override suspend fun readValue(
                            key: String,
                        ): String? {
                            return context.getData(key)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Koin.kt

                    single<AppDataStore> { AppDataStoreManager(context) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain

                    //Context.kt
                    import platform.darwin.NSObject

                    actual typealias Context = NSObject

                    //DataStore.kt
                    import platform.Foundation.NSUserDefaults
                    import kotlinx.coroutines.flow.MutableSharedFlow

                    actual suspend fun Context.putData(key: String, `object`: String) {
                        val sharedFlow = MutableSharedFlow<String>()
                        NSUserDefaults.standardUserDefaults().setObject(`object`, key)
                        sharedFlow.emit(`object`)
                    }

                    actual suspend inline fun Context.getData(key: String): String? {
                        return NSUserDefaults.standardUserDefaults().stringForKey(key)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class LoginInteractor(
                        private val appDataStoreManager: AppDataStore
                    ) {
                        appDataStoreManager.setValue( "KEY", "VALUE" )
                        val token = appDataStoreManager.readValue("KEY") ?: ""
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/santimattius/kmp-kvs-storage.git
//https://github.com/ben-burwood/Tasks